<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { aliOssImageCompress } from '@/utils/aliOssImageCompress'

const props = withDefaults(defineProps<{
  /** 是否展示（外层用 Transition 淡出） */
  visible: boolean
  /** 静态封面地址，空则用引擎默认底图 */
  coverUrl?: string
  /** Loading 动态封面 MP4，空则不渲染 */
  animationUrl?: string
  /** 进度 0-100 */
  percent: number
  /** 底部轮播文案 */
  tip?: string
}>(), {
  coverUrl: '',
  animationUrl: '',
  tip: '',
})

// ===== loading 背景：与详情页（BookPage.vowpath）同一套实现 =====
// 清晰封面宽度铺满、高度自适应顶对齐（不用 contain，避免左右半像素空隙露底色）；其上叠 OSS 高斯模糊封面（cover），用纵向渐变遮罩只在下半部分显现
// 封面固定 9:16 顶对齐，全面屏上封面底缘约在 84%，遮罩终点下移到 80% 让封面标题尽量保持清晰
const BLUR_STOP_START = 66
const BLUR_STOP_END = 80

const loadingRef = ref<HTMLElement>()
const panelRef = ref<HTMLElement>()
const loadingHeight = ref(0)
const panelHeight = ref(0)

const compressedCoverUrl = computed(() => props.coverUrl ? aliOssImageCompress(props.coverUrl) : '')

// 渐变终点取「80%」与「底部内容区顶缘」中更靠上的一个，保证进度条/文案落在模糊区内
const blurStops = computed(() => {
  const span = BLUR_STOP_END - BLUR_STOP_START
  let end = BLUR_STOP_END
  if (loadingHeight.value && panelHeight.value) {
    const panelTop = ((loadingHeight.value - panelHeight.value - 8) / loadingHeight.value) * 100
    end = Math.min(end, panelTop)
  }
  end = Math.max(end, span)
  return { start: end - span, end }
})

const bgStyle = computed(() => {
  const url = compressedCoverUrl.value || '/content/read/bg.jpg'
  return `background: url(${url}) no-repeat center top; background-size: 100% auto;`
})

// 模糊封面走 OSS 处理（与详情页/APP 同参数：缩到 200 宽后高斯模糊）
const blurCoverUrl = computed(() => {
  if (!props.coverUrl)
    return ''
  const base = props.coverUrl.split('?')[0]
  return `${base}?x-oss-process=image/resize,w_200/blur,r_25,s_15/format,webp`
})

const blurBgStyle = computed(() => {
  if (!blurCoverUrl.value)
    return ''
  const grad = `linear-gradient(to bottom, transparent ${blurStops.value.start.toFixed(1)}%, #000 ${blurStops.value.end.toFixed(1)}%)`
  return `background: url(${blurCoverUrl.value}) no-repeat center top; background-size: cover;`
    + ` mask-image: ${grad}; -webkit-mask-image: ${grad};`
})

// 底部内容区高度随 tips 行数变化，实时测量以调整模糊层渐变位置
let resizeObserver: ResizeObserver | undefined
function observeLoadingLayout() {
  function update() {
    loadingHeight.value = loadingRef.value?.clientHeight || 0
    panelHeight.value = panelRef.value?.offsetHeight || 0
  }
  update()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(update)
    if (loadingRef.value)
      resizeObserver.observe(loadingRef.value)
    if (panelRef.value)
      resizeObserver.observe(panelRef.value)
  }
}

onMounted(observeLoadingLayout)
onUnmounted(() => resizeObserver?.disconnect())
</script>

<template>
  <Transition name="fade">
    <div v-if="visible" ref="loadingRef" class="loading-overlay">
      <div class="loading-bg absolute inset-0" :style="bgStyle" />
      <!-- Loading 动态封面：出首帧后淡入盖在静态封面上；失败/超时静默回落，底部模糊层不变 -->
      <BizCoverVideo v-if="animationUrl" :src="animationUrl" class="loading-bg-video" />
      <div class="loading-bg-blur absolute inset-0" :style="blurBgStyle" />
      <div class="spacer" />
      <div ref="panelRef" class="loading-panel">
        <div class="tips-text">
          {{ tip }}
        </div>
        <div class="tips-gap" />
        <div class="progress-container">
          <div class="progress-bar" :style="{ width: `${percent}%` }" />
          <div class="progress-text" :style="{ left: `${percent}%` }">
            {{ percent }}%
          </div>
        </div>
        <div class="loading-label">
          loading...
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  // 兜底底色：背景图未加载完时蒙层即保持不透明，避免下层引擎页 loading 透上来造成重叠
  background-color: #231F20;
  color: white;
  font-family: Arial, sans-serif;

  // 封面各层均只作背景，不接收交互；z-index -1 位于兜底底色之上、内容之下
  .loading-bg,
  .loading-bg-video,
  .loading-bg-blur {
    z-index: -1;
    pointer-events: none;
  }
}

.spacer {
  margin-top: auto;
}

.loading-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.tips-text {
  max-width: 75%;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.tips-gap {
  margin-bottom: 3vh;
}

.progress-container {
  position: relative;
  width: 80%;
  max-width: 310px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 3vh;
}

.progress-bar {
  height: 14px;
  background: linear-gradient(to right, #C68BFF, #FF8DDC);
  border-radius: 20px;
  transition: width 0.2s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  height: 14px;
  line-height: 14px;
  font-size: 12px;
  color: white;
  white-space: nowrap;
  transform: translateX(-110%);
  transition: left 0.2s ease;
  pointer-events: none;
}

.loading-label {
  margin-bottom: 5vh;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
